import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ShieldCheck, LogOut, LayoutGrid, ArrowRight, TrendingUp, Landmark, Activity, Package, ShieldAlert } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { fetchAdminSites, fetchSalesPipeline, fetchComplianceReminders, fetchAdminOperationsOverview, fetchAdminCommerceOverview, fetchMonitoringOverview } from "@/lib/api";
import AdminNavTabs from "@/components/AdminNavTabs";

export default function AdminDashboard() {
  const { token, email, checking, isLoggedIn, revealAdminUi, logout } = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [operations, setOperations] = useState(null);
  const [commerce, setCommerce] = useState(null);
  const [monitoring, setMonitoring] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetchAdminSites(token).then(setSummary).catch(() => {});
    fetchSalesPipeline(token).then(setSalesSummary).catch(() => {});
    fetchComplianceReminders(token).then(setReminders).catch(() => {});
    fetchAdminOperationsOverview(token).then(setOperations).catch(() => {});
    fetchAdminCommerceOverview(token).then(setCommerce).catch(() => {});
    fetchMonitoringOverview(token).then(setMonitoring).catch(() => {});
  }, [token]);

  if (checking) return null;
  // DEBUG-ONLY: revealAdminUi (isLoggedIn || debug toggle) lets the page shell render for
  // review without a real session — data fetches above still need a real `token` and stay
  // empty/"Loading…" without one. Real API access is unaffected. See useAdminAuth.js.
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5 text-emerald-700"><ShieldCheck size={20} /><span className="text-xs font-bold uppercase tracking-[0.16em]">{isLoggedIn ? `Logged in as ${email}` : "DEBUG — Admin UI revealed, no real session"}</span></div>
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"><LogOut size={15} /> Log out</button>
        </div>
        <h1 className="mt-4 font-display text-3xl md:text-4xl font-bold">Admin</h1>
        <AdminNavTabs />

        <Link to="/admin/sites" className="mt-8 block rounded-xl border border-slate-200 bg-white p-6 hover:border-emerald-400 transition-colors">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 font-semibold text-lg"><LayoutGrid size={18} className="text-emerald-600" /> Website Management</h2>
              {summary ? (
                <p className="mt-2 text-sm text-slate-600">
                  {summary.total} managed sites — {summary.counts.offline_concept} concepts, {summary.counts.proposal_accepted} accepted, {summary.counts.online_client} online, {summary.counts.rejected} rejected.
                </p>
              ) : (
                <p className="mt-2 text-sm text-slate-500">Loading…</p>
              )}
            </div>
            <ArrowRight size={18} className="text-slate-400" />
          </div>
        </Link>

        <Link to="/admin/sales" className="mt-4 block rounded-xl border border-slate-200 bg-white p-6 hover:border-emerald-400 transition-colors">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 font-semibold text-lg"><TrendingUp size={18} className="text-emerald-600" /> Sales Pipeline</h2>
              {salesSummary ? (
                <p className="mt-2 text-sm text-slate-600">
                  {salesSummary.total} prospects — {salesSummary.stage_counts.not_contacted} not contacted, {salesSummary.stage_counts.replied + salesSummary.stage_counts.conversation + salesSummary.stage_counts.proposal_sent} in active conversation, {salesSummary.stage_counts.won} won.
                  {salesSummary.follow_ups_overdue > 0 && <span className="ml-1 font-semibold text-rose-600">{salesSummary.follow_ups_overdue} overdue follow-up{salesSummary.follow_ups_overdue === 1 ? "" : "s"}.</span>}
                </p>
              ) : (
                <p className="mt-2 text-sm text-slate-500">Loading…</p>
              )}
            </div>
            <ArrowRight size={18} className="text-slate-400" />
          </div>
        </Link>

        <Link to="/admin/finance" className="mt-4 block rounded-xl border border-slate-200 bg-white p-6 hover:border-emerald-400 transition-colors">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 font-semibold text-lg"><Landmark size={18} className="text-emerald-600" /> Finance & BIR Tax</h2>
              {reminders ? (
                reminders.count === 0 ? (
                  <p className="mt-2 text-sm text-slate-600">No compliance reminders due.</p>
                ) : (
                  <p className="mt-2 text-sm text-slate-600">
                    {reminders.count} compliance reminder{reminders.count === 1 ? "" : "s"}.
                    {reminders.overdue_count > 0 && <span className="ml-1 font-semibold text-rose-600">{reminders.overdue_count} overdue.</span>}
                  </p>
                )
              ) : (
                <p className="mt-2 text-sm text-slate-500">Loading…</p>
              )}
            </div>
            <ArrowRight size={18} className="text-slate-400" />
          </div>
        </Link>

        <Link to="/admin/monitoring" className="mt-4 block rounded-xl border border-slate-200 bg-white p-6 hover:border-emerald-400 transition-colors" data-testid="monitoring-summary-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 font-semibold text-lg"><ShieldAlert size={18} className="text-emerald-600" /> Website Operations</h2>
              {monitoring ? (
                <p className="mt-2 text-sm text-slate-600">
                  {(monitoring.severity_counts?.critical || 0) + (monitoring.severity_counts?.high || 0) + (monitoring.severity_counts?.warning || 0) + (monitoring.severity_counts?.info || 0) === 0 ? (
                    "All monitored websites healthy."
                  ) : (
                    <>Healthy overall.{monitoring.needs_manual_action_count > 0 && <span className="ml-1 font-semibold text-rose-600">{monitoring.needs_manual_action_count} need{monitoring.needs_manual_action_count === 1 ? "s" : ""} your attention.</span>}</>
                  )}
                  {monitoring.monitoring_self_health?.is_stale && <span className="ml-1 font-semibold text-amber-600">Monitoring itself looks stale.</span>}
                </p>
              ) : (
                <p className="mt-2 text-sm text-slate-500">Loading…</p>
              )}
            </div>
            <ArrowRight size={18} className="text-slate-400" />
          </div>
        </Link>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="operations-overview-card">
          <h2 className="flex items-center gap-2 font-semibold text-lg mb-3"><Activity size={18} className="text-emerald-600" /> Client Operations</h2>
          {!operations ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><p className="text-2xl font-bold font-display">{operations.active_clients}</p><p className="text-xs text-slate-500">Active clients</p></div>
                <div><p className={`text-2xl font-bold font-display ${operations.sites_requiring_attention.length > 0 ? "text-amber-600" : ""}`}>{operations.sites_requiring_attention.length}</p><p className="text-xs text-slate-500">Sites requiring attention</p></div>
                <div><p className={`text-2xl font-bold font-display ${operations.pending_appointments > 0 ? "text-amber-600" : ""}`}>{operations.pending_appointments}</p><p className="text-xs text-slate-500">Pending appointment requests</p></div>
                <div><p className="text-2xl font-bold font-display">{operations.new_leads_7d}</p><p className="text-xs text-slate-500">New leads (7 days)</p></div>
              </div>
              {operations.sites_requiring_attention.length > 0 && (
                <p className="mt-3 text-xs text-amber-700">No business modules configured yet for: {operations.sites_requiring_attention.join(", ")}. <Link to="/admin/sites" className="underline">Open Website Management</Link> to set them up.</p>
              )}
              {operations.recent_activity.length > 0 && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Recent activity</p>
                  <ul className="space-y-1 text-sm text-slate-600">
                    {operations.recent_activity.slice(0, 6).map((a, i) => (
                      <li key={i}>{a.type === "appointment_request" ? "Appointment request" : "New lead"} — {a.site} <span className="text-slate-400 text-xs">({a.status})</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="commerce-overview-card">
          <h2 className="flex items-center gap-2 font-semibold text-lg mb-3"><Package size={18} className="text-emerald-600" /> Commerce Health</h2>
          {!commerce ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <>
              <p className="text-sm text-slate-600 mb-3">{commerce.client_commerce_modules_active} client{commerce.client_commerce_modules_active === 1 ? "" : "s"} with a commerce module active.</p>
              {commerce.sites_with_inventory_or_order_warnings.length === 0 ? (
                <p className="text-sm text-slate-500">No inventory or order warnings.</p>
              ) : (
                <ul className="space-y-1 text-sm text-slate-700">
                  {commerce.sites_with_inventory_or_order_warnings.map((s) => (
                    <li key={s.website_slug}>
                      <strong>{s.business_name}</strong>:
                      {s.low_stock > 0 && ` ${s.low_stock} low stock`}
                      {s.out_of_stock > 0 && ` · ${s.out_of_stock} out of stock`}
                      {s.pending_orders > 0 && ` · ${s.pending_orders} pending order${s.pending_orders === 1 ? "" : "s"}`}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-xs text-slate-400">MySiteCare admin sees this summary only — each business manages its own order processing in its own dashboard.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
